import { CameraGimbalSchema } from '../../../renderer/src/camera-gimbal-contract';
import { CinematicSequenceSchema } from '../../../renderer/src/cinematic-sequence-contract';
import { PlaceTitleSchema } from '../../../renderer/src/place-title';
import { savedAccountRide } from './account-import';
import { CameraAngleSchema } from '../../../renderer/src/camera-angle-contract';
import { DepthOfFieldSchema } from '../../../renderer/src/depth-of-field-contract';
import { enabledDepthOfField } from '../../../renderer/src/depth-of-field';
import { TrackAppearanceSchema } from '../../../renderer/src/track-appearance-contract';
import { API, apiRequest as baseApiRequest, jsonPost } from './api-client';
import type { VideoJob, VideoRequest } from './local-video';
import { VideoPresentationSchema } from '../../../renderer/src/presentation-contract';
import { RENDER_RELEASE } from '../../../renderer/src/render-release.generated';
import { RENDER_RELEASE_HEADER, releaseHeader } from '../../../renderer/src/release-contract';

export function cloudRenderClient(prefix = '') {
const apiRequest: typeof baseApiRequest = (path, options) => baseApiRequest(prefix + path, options);

interface Session { authenticated: boolean; operator: boolean; accountId: string }
const session = () => apiRequest<Session>(`${API}/session`);
const cloudVideos = (before?: string, state?: string) => apiRequest<{ jobs: VideoJob[]; nextCursor: string | null }>(`${API}/render-jobs?${new URLSearchParams({ ...(before ? { before } : {}), ...(state ? { state } : {}) })}`);
const cloudAction = (id: string, action: 'cancel' | 'retry') => apiRequest<VideoJob>(`${API}/render-jobs/${encodeURIComponent(id)}/${action}`, {
  ...jsonPost({}), headers: { 'Content-Type': 'application/json', [RENDER_RELEASE_HEADER]: releaseHeader(RENDER_RELEASE) },
});

interface CloudRenderOutput {
  cameraStyleVersion?: 3 | 4;
  placeTitle?: VideoRequest['placeTitle'];
  exportRange?: VideoRequest['exportRange'];
  cinematicSequence?: VideoRequest['cinematicSequence'];
  depthOfField?: VideoRequest['depthOfField'];
  cameraGimbal?: VideoRequest['cameraGimbal'];
  cameraAngle?: number | undefined;
  trackAppearance?: VideoRequest['trackAppearance'];
  presentation?: VideoRequest['presentation'];
  unitSystem?: "metric" | "imperial" | undefined;
  preset: string;
  cameraStyle: string;
  cameraDistance: string;
  lineStyle: string;
  mapStyle: string;
  terrainExaggeration?: number | undefined;
  hidePlaces?: boolean | undefined;
  durationSeconds: number;
}

async function submitCloudRender(rideId: string, title: string, output: CloudRenderOutput, key: string): Promise<string> {
  const response = await apiRequest<{ jobId: string }>(API + '/render-jobs', {
    ...jsonPost({ rideId, title, output }),
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key, [RENDER_RELEASE_HEADER]: releaseHeader(RENDER_RELEASE) },
  });
  if (typeof response.jobId !== 'string' || !response.jobId) throw new Error('The video service did not confirm your submission.');
  return response.jobId;
}

// Retains the submission key and uploaded ride for a retry after an uncertain network response.
// A new presentation or new FIT gets its own idempotency key.
let pending: { signature: string; rideId?: string; jobId?: string; key: string } | undefined;
async function submitCloudVideo(input: VideoRequest): Promise<string> {
  const placeTitle = input.placeTitle === undefined ? undefined : PlaceTitleSchema.parse(input.placeTitle);
  const depthOfField = enabledDepthOfField(input.depthOfField === undefined ? undefined : DepthOfFieldSchema.parse(input.depthOfField));
  const cinematicSequence = input.cinematicSequence === undefined ? undefined : CinematicSequenceSchema.parse(input.cinematicSequence);
  const cameraGimbal = CameraGimbalSchema.parse(input.cameraGimbal ?? 'glide');
  const cameraAngle = input.cameraAngle === undefined ? undefined : CameraAngleSchema.parse(input.cameraAngle);
  const trackAppearance = input.trackAppearance ? TrackAppearanceSchema.parse(input.trackAppearance) : undefined;
  const presentation = input.presentation ? VideoPresentationSchema.parse(input.presentation) : undefined;
  const signature = JSON.stringify([...(placeTitle ? [placeTitle] : []), ...(input.exportRange ? [input.exportRange] : []), ...(cinematicSequence ? [cinematicSequence] : []), input.cameraStyleVersion ?? 4, input.ride.id, input.ride.title, input.preset, input.camera, input.distance, cameraGimbal, cameraAngle, input.line, input.mapStyle, input.terrainExaggeration, input.hidePlaces ?? false, input.unitSystem ?? "metric", input.durationSeconds, ...(presentation ? [presentation] : []), ...(trackAppearance ? [trackAppearance] : []), ...(depthOfField ? [depthOfField] : [])]);
  if (pending?.signature !== signature) pending = { signature, key: crypto.randomUUID() };
  const submission = pending;
  if (submission.jobId) return submission.jobId;
  const savedRide=prefix ? undefined : savedAccountRide(input.ride.id);
  if (!submission.rideId && savedRide) submission.rideId=savedRide.rideId;
  if (!submission.rideId) {
    const { id, title, region, source, sourceFileName, recordingDevice, timerDurationSeconds, points, sourcePoints } = input.ride;
    submission.rideId = (await apiRequest<{ rideId: string }>(`${API}/rides`, jsonPost({ id, title, region, source, sourceFileName, recordingDevice, timerDurationSeconds, points: sourcePoints ?? points }))).rideId;
  }
  submission.jobId = await submitCloudRender(submission.rideId, input.ride.title, {
    placeTitle,
    exportRange: input.exportRange,
    cameraStyleVersion: input.cameraStyleVersion ?? 4, preset: input.preset, cameraStyle: input.camera, cameraDistance: input.distance,
    cameraGimbal,
    cameraAngle,
    cinematicSequence,
    depthOfField,
    trackAppearance,
    terrainExaggeration: input.terrainExaggeration,
    hidePlaces: input.hidePlaces,
    unitSystem: input.unitSystem,
    presentation,
    lineStyle: input.line, mapStyle: input.mapStyle, durationSeconds: input.durationSeconds,
  }, submission.key);
  return submission.jobId;
}

async function cloudVideo(id: string): Promise<VideoJob> {
  const job = await apiRequest<VideoJob>(API + '/render-jobs/' + encodeURIComponent(id));
  if (pending?.jobId === id) pending = undefined;
  return job;
}

async function createCloudVideo(input: VideoRequest): Promise<VideoJob> {
  return cloudVideo(await submitCloudVideo(input));
}

return { session, cloudVideos, cloudAction, submitCloudVideo, cloudVideo, createCloudVideo };
}
const standard = cloudRenderClient();
export const { session, cloudVideos, cloudAction, submitCloudVideo, cloudVideo, createCloudVideo } = standard;
